import React, { useState, useEffect } from 'react';
import { GridItem, SimpleGrid, VStack, Text, Flex, Heading } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { AnimatePresence } from 'framer-motion';
import { CheckoutPayment } from './CheckoutPayment';
import {
  useCheckoutDispatch,
  useNatsState,
  useSavedDineInContextState,
} from '@olo-web/client-state';
import { CheckoutDisclaimer, CheckoutOrderButton } from './common';
import { useIsOrderButtonHidden } from './common/hooks/useIsOrderButtonHidden';
import { Checkout } from './Checkout.ui';
import { usePaymentMethodValues } from '@olo-web/domain/payments/hooks';
import { useSendEvent } from '@olo-web/utils/common/hooks';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { ProductTotals } from '@olo-web/components/molecules/ProductTotals';
import { ApplyOffersButton } from './ApplyOffersButton';
import { TipsSection } from '@olo-web/components/atoms/TipsSection';
import { CheckoutBagContainer } from '@templates/Checkout/CheckoutBagContainer';
import { AnimateHeight } from '@olo-web/components/atoms/AnimateHeight';
import { GuestTotals } from './GuestTotals/GuestTotals.ui';
import { useIsSplitByAmount } from '@olo-web/utils/common/hooks/useIsSplitByAmount';
import { GuestTotalsList } from './GuestTotals/GuestTotalsList.ui';
import { useCheckedGuests } from '@olo-web/domain/orders/hooks/useCheckedGuests';
import { useTipAmount } from '@olo-web/domain/orders/hooks/useTipAmount';
import { formatPrice } from '@olo-web/domain/orders/functions/formatPrice';
import { useUpdateOrderStatus } from '@olo-web/domain/orders/mutations/useUpdateOrderStatus';
import { EOrderStatus } from '@olo-web/types/enums/orderStatus.enum';
import { EAnalyticsEventNames } from '@olo-web/types/enums/analyticsEventNames.enum';
import { EGALocations } from '@olo-web/types/enums/googleAnalyticsLocations.enum';
import { useUnload } from '@olo-web/utils/common/hooks/useUnload';
import { useUnmount } from 'react-use';
import { useIsDineIn } from '@olo-web/utils/common/hooks/useIsDineIn';
import { useGoToConfirmation } from '@olo-web/utils/common/hooks/useGoToConfirmation';
import { useGoToMenu } from '@olo-web/utils/common/hooks/useGoToMenu';
import { ECheckoutTypes } from '@olo-web/types/enums';

const SP = 6;

const PlaceOrderSection = ({ handleOrderClick, isSplitByAmount, onErrorRequest }) => {
  const checkoutDispatch = useCheckoutDispatch();

  return (
    <VStack>
      <CheckoutOrderButton
        onClick={handleOrderClick}
        onSuccess={() => {
          if (isSplitByAmount) {
            checkoutDispatch({ type: 'SET_CHECKED_GUESTS', payload: [] });
          }
        }}
        onError={onErrorRequest}
      />
      <VStack mt={8} spacing={4} id="place-order-container" px={{ base: 3, md: 0 }}>
        <CheckoutDisclaimer />
      </VStack>
    </VStack>
  );
};

export const CheckoutDinein = () => {
  const { data: merchant } = useMerchant();
  const isOrderButtonHidden = useIsOrderButtonHidden();
  const { giftCardCoversEntireOrderAmount, isCash, order, isZeroChargeOrder, isOrderLoading } =
    usePaymentMethodValues();
  const shouldShowGiftCard = !isCash && merchant?.giftCardPaymentEnabled;
  const { guests, total } = useCheckedGuests();
  const router = useRouter();
  const goToMenu = useGoToMenu({ resetOrderStatus: true });
  const tipAmount = useTipAmount();
  const shouldShowPaymentMethod = !giftCardCoversEntireOrderAmount && !isZeroChargeOrder;
  const showPlaceOrderButton = !isOrderButtonHidden || order?.balanceDueAmount === '0';
  const isSplitByAmount = useIsSplitByAmount();
  const [orderButtonClicked, setOrderButtonClicked] = useState(false);
  const setClickState = () => setOrderButtonClicked(true);
  const { mutate: updateOrderStatus } = useUpdateOrderStatus();
  const { sendEvent } = useSendEvent();
  const savedDineInState = useSavedDineInContextState();
  const goToConfirmation = useGoToConfirmation();
  const natsState = useNatsState();
  const isDineIn = useIsDineIn();
  const [hasSetStatus, setHasSetStatus] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const checkoutDispatch = useCheckoutDispatch();

  useEffect(() => {
    if (isSplitByAmount) {
      checkoutDispatch({ type: 'REMOVE_PAYMENT' });
    }
  }, [isSplitByAmount, checkoutDispatch]);

  useEffect(() => {
    if (isDineIn && order && !hasSetStatus) {
      if (isSplitByAmount) {
        if (order?.groupOrderInfo?.status !== EOrderStatus.GROUP_PAYING) {
          updateOrderStatus({
            status: EOrderStatus.GROUP_PAYING,
          });
        }
      } else if (order?.groupOrderInfo?.status !== EOrderStatus.IN_CHECKOUT) {
        updateOrderStatus({
          status: EOrderStatus.IN_CHECKOUT,
        });
      }
      setHasSetStatus(true);
    }
  }, [order, updateOrderStatus, isDineIn, isSplitByAmount, setHasSetStatus, hasSetStatus]);

  useEffect(() => {
    if (!isOrderLoading && !order && natsState.topic === null && !natsState.natsConnectionStarted) {
      if (router.query.orderId) {
        goToConfirmation();
      } else {
        goToMenu();
      }
    }
  }, [
    order,
    natsState.topic,
    natsState.natsConnectionStarted,
    isOrderLoading,
    goToConfirmation,
    goToMenu,
    router.query.orderId,
  ]);

  useEffect(() => {
    if (order?.groupOrderInfo?.status === EOrderStatus.COMPLETED) {
      goToConfirmation();
    }
  }, [order?.groupOrderInfo?.status, goToConfirmation]);

  useEffect(() => {
    if (
      !merchant?.dineinSplitPaymentEnabled &&
      router.query.checkoutType === ECheckoutTypes.SPLIT_BY_AMOUNT
    ) {
      delete router.query.checkoutType;
      router.replace(
        {
          pathname: router.pathname,
          query: router.query,
        },
        undefined,
        { shallow: true }
      );
    }
  }, [merchant?.dineinSplitPaymentEnabled, router, router.query.checkoutType]);

  const setOrderBuilding = () => {
    if (!orderButtonClicked) {
      updateOrderStatus({
        status: EOrderStatus.BUILDING,
      });
      sendEvent(EAnalyticsEventNames.ORDER_UNLOCKED, {
        googleAnalytics: {
          location: EGALocations.DINE_IN,
          action: 'click',
          object: EAnalyticsEventNames.ORDER_UNLOCKED,
          eventMetadata: {
            initiator: savedDineInState?.guest,
            dineInType: merchant?.dineinPayLaterEnabled ? 'Pay Later' : 'Pay Now',
          },
        },
      });
    }
  };
  useUnmount(setOrderBuilding);
  useUnload(setOrderBuilding);

  const handleOrderClick = () => {
    setClickState();

    if (isSplitByAmount) {
      setIsSubmittingOrder(true);
    }
  };

  const onErrorRequest = () => {
    if (isSplitByAmount) {
      setIsSubmittingOrder(false);
    }

    setOrderButtonClicked(false);
  };

  return (
    <Checkout>
      <Heading
        id="main-content"
        key="title"
        as="h1"
        my={{ base: 4, md: 6 }}
        ml={{ base: 4, md: 0 }}
        fontSize={{ base: 24, md: 30 }}
        fontWeight="600"
      >
        Pay the bill
      </Heading>
      <SimpleGrid
        rowGap={SP}
        columnGap={{
          base: 0,
          md: SP,
        }}
        templateColumns={{
          base: 'repeat(1, 1fr)',
          md: 'repeat(3, 1fr)',
        }}
        pos="relative"
      >
        <AnimatePresence>
          <GridItem colSpan={2} key="main">
            <VStack spacing={SP} alignItems="stretch">
              {isSplitByAmount ? (
                <GuestTotals />
              ) : (
                <CheckoutBagContainer contentTitle={'Your bill'} showDivider spacing={2}>
                  <ProductTotals
                    includeTip={showPlaceOrderButton}
                    includeOtherPayments
                    includeGiftCard
                  />
                  <ApplyOffersButton />
                </CheckoutBagContainer>
              )}
              <CheckoutPayment
                isDisabled={isZeroChargeOrder}
                shouldShowPaymentMethod={shouldShowPaymentMethod}
                shouldShowGiftCard={shouldShowGiftCard}
                shouldShowContinueButton={isOrderButtonHidden}
                showEmailField={true}
              />
            </VStack>
          </GridItem>
          <GridItem colSpan={1} id="secondary">
            {showPlaceOrderButton ? (
              <>
                {((isSplitByAmount && total > 0) || isSubmittingOrder) && (
                  <CheckoutBagContainer contentTitle={'Your bill'} showDivider spacing={2}>
                    <VStack as="table" w="100%" tabIndex={0}>
                      <GuestTotalsList guests={guests} />
                      {
                        <AnimateHeight
                          key="scan-and-pay-tips"
                          transition={{ duration: 0.3, delay: 0.3 }}
                        >
                          <TipsSection radioGroupProps={{ mt: 1 }} withoutTitle />
                        </AnimateHeight>
                      }
                      <Flex
                        width="100%"
                        justifyContent="space-between"
                        fontWeight="600"
                        fontSize="md"
                        data-testid="dinein-order-total-text"
                      >
                        <Text>Total</Text>
                        {formatPrice(total + +tipAmount)}
                      </Flex>
                    </VStack>
                    <PlaceOrderSection
                      handleOrderClick={handleOrderClick}
                      isSplitByAmount={isSplitByAmount}
                      onErrorRequest={onErrorRequest}
                    />
                  </CheckoutBagContainer>
                )}
                {!isSplitByAmount && (
                  <AnimateHeight key="scan-and-pay-tips" transition={{ duration: 0.3, delay: 0.3 }}>
                    <TipsSection withContentCard>
                      <PlaceOrderSection
                        handleOrderClick={handleOrderClick}
                        isSplitByAmount={isSplitByAmount}
                        onErrorRequest={onErrorRequest}
                      />
                    </TipsSection>
                  </AnimateHeight>
                )}
              </>
            ) : null}
          </GridItem>
        </AnimatePresence>
      </SimpleGrid>
    </Checkout>
  );
};
